import { Check, Loader2 } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useState } from 'react';
import { sanitizeLetterHtml } from "@/utils/htmlUtils";
import { PREMIUM_ANNUAL_DISPLAY, SINGLE_LETTER_DISPLAY } from "@/utils/publicPrices";

interface PricingOption {
  title: string;
  price: string;
  features: string[];
  recommended?: boolean;
  planType: 'email' | 'premium';
}

interface PricingCardProps {
  content: string;
  deliveryDate: string;
  email: string;
  whatsapp: string;
  setErrors: React.Dispatch<React.SetStateAction<{ content: boolean; deliveryDate: boolean; email: boolean; whatsapp: boolean }>>;
}

export default function PricingCard({ content, deliveryDate, email, whatsapp, setErrors }: PricingCardProps) {
  const { toast } = useToast();
  const [loadingPlan, setLoadingPlan] = useState<'email' | 'premium' | null>(null);

  const options: PricingOption[] = [
    {
      title: "Entrega por Email",
      price: SINGLE_LETTER_DISPLAY,
      planType: "email",
      features: [
        "Pagamento único",
        "Entrega por email", 
        "Escolha a data de entrega",
        "Formato de email elegante"
      ],
    },
    {
      title: "Entrega Premium",
      price: PREMIUM_ANNUAL_DISPLAY,
      planType: "premium",
      features: [
        "Entrega por email",
        "Entrega por WhatsApp",
        "Armazene cartas para sempre",
        "Suporte premium",
        "Múltiplas datas de entrega"
      ],
      recommended: true,
    }
  ];

  const handleCheckout = async (planType: 'email' | 'premium') => {
    try {
      setLoadingPlan(planType);
      console.log('Iniciando checkout para o plano:', planType);
      
      if (!content || !deliveryDate || !email || !whatsapp) {
        setErrors((prev) => ({
          ...prev,
          content: !content || content.length < 10,
          deliveryDate: !deliveryDate,
          email: !email,
          whatsapp: !whatsapp,
        }));
        console.log('Campos obrigatórios faltando:', { 
          content: !!content, 
          deliveryDate: !!deliveryDate, 
          email: !!email,
          whatsapp: !!whatsapp
        });
        
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Por favor, preencha todos os campos obrigatórios.",
        });
        setLoadingPlan(null);
        return;
      }

      if (content.length < 10) {
        setErrors((prev) => ({ ...prev, content: true }));
        toast({
          variant: "destructive",
          title: "Carta muito curta",
          description: "Por favor, escreva uma mensagem mais longa para o seu futuro.",
        });
        setLoadingPlan(null);
        return;
      }

      console.log('Enviando dados para checkout:', { 
        planType, 
        email, 
        deliveryDate,
        contentLength: content.length,
        hasWhatsApp: !!whatsapp,
      });
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: JSON.stringify({
          content: sanitizeLetterHtml(content),
          deliveryDate,
          email,
          whatsapp,
          planType,
        }),
      });

      if (error) {
        console.error('Erro na função create-checkout:', error);
        setLoadingPlan(null);
        throw error;
      }

      console.log('Resposta do checkout:', data);

      if (data?.url) {
        console.log('Redirecionando para:', data.url);
        window.location.href = data.url;
      } else {
        console.error('URL do checkout não encontrada na resposta');
        setLoadingPlan(null);
        throw new Error('URL do checkout não encontrada');
      }
    } catch (error) {
      console.error('Erro detalhado do checkout:', error);
      setLoadingPlan(null);
      toast({
        variant: "destructive",
        title: "Erro no Checkout",
        description: error instanceof Error ? error.message : "Ocorreu um erro ao processar o pagamento. Por favor, tente novamente.",
      });
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
      {options.map((option) => (
        <div
          key={option.title}
          className={`bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg p-8 border flex flex-col ${
            option.recommended ? 'border-blue-200 shadow-blue-100' : 'border-gray-100'
          }`}
        >
          {option.recommended && (
            <span className="bg-blue-50 text-blue-600 px-4 py-1.5 rounded-full text-sm font-medium border border-blue-100">
              Recomendado
            </span>
          )}
          <h3 className="text-xl font-bold mt-4">{option.title}</h3>
          <p className="text-3xl font-bold mt-4 bg-gradient-to-r from-gray-900 to-gray-600 text-transparent bg-clip-text">
            {option.price}
          </p>
          <ul className="mt-6 space-y-4 flex-grow">
            {option.features.map((feature) => (
              <li key={feature} className="flex items-center text-gray-600">
                <div className={`rounded-full p-1 mr-3 ${
                  option.recommended ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-600'
                }`}>
                  <Check className="h-4 w-4" />
                </div>
                {feature}
              </li>
            ))}
          </ul>
          <button
            onClick={() => handleCheckout(option.planType)}
            disabled={loadingPlan !== null}
            className={`w-full mt-8 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center ${
              option.recommended
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-xl hover:shadow-blue-200 disabled:bg-blue-400'
                : 'bg-gray-50 text-gray-900 hover:bg-gray-100 border border-gray-200 disabled:bg-gray-100'
            }`}
          >
            {loadingPlan === option.planType ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processando...
              </>
            ) : (
              'Escolher Plano'
            )}
          </button>
        </div>
      ))}
    </div>
  );
}